import React from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';

import { ESignatureIntegration } from '../types';

export interface StatusAndActionsProps {
  disabled: boolean;
  integration?: ESignatureIntegration;
  isConnected: boolean;
  isActive: boolean;
  isLoading: boolean;
  isCompanyConnecting?: boolean;
  onConnect: () => void;
  onDelete: () => void;
}

const isRealIntegration = (integration?: ESignatureIntegration): boolean =>
  Boolean(integration?.id && !integration.id.startsWith('mock-'));

const hasValidationError = (integration?: ESignatureIntegration): boolean =>
  Boolean(
    isRealIntegration(integration) &&
    integration?.integrationType === 'company' &&
    (integration?.error || !integration?.isActive),
  );

const shouldShowDeleteButton = (
  isConnected: boolean,
  integration?: ESignatureIntegration,
): boolean => isConnected || hasValidationError(integration);

export const StatusAndActions = ({
  disabled = false,
  integration,
  isActive = false,
  isConnected = false,
  isLoading = false,
  isCompanyConnecting = false,
  onConnect,
  onDelete,
}: StatusAndActionsProps) => {
  const intl = useIntl();
  if (disabled) {
    return (
      <Layout align="center" spacing={4}>
        <Chip
          mode="tag"
          status="info"
          text={intl.formatMessage({
            id: 'StatusAndActions.notConnected',
            defaultMessage: 'Not connected',
            description: 'Status chip text for disconnected integration',
          })}
        />
      </Layout>
    );
  }

  const items = [];

  if (isCompanyConnecting) {
    items.push(
      <Chip
        key="not-connected-connecting"
        mode="tag"
        status="info"
        text={intl.formatMessage({
          id: 'StatusAndActions.notConnected',
          defaultMessage: 'Not connected',
          description: 'Status chip text for disconnected integration',
        })}
      />,
    );
  } else if (hasValidationError(integration)) {
    items.push(
      <Chip
        key="error"
        mode="tag"
        status="danger"
        text={intl.formatMessage({
          id: 'StatusAndActions.validationError',
          defaultMessage: 'Validation error',
          description: 'Error chip text for validation errors in integration',
        })}
      />,
    );
  } else if (isConnected) {
    items.push(
      <Chip
        key="connected"
        mode="tag"
        status="success"
        text={intl.formatMessage({
          id: 'StatusAndActions.connected',
          defaultMessage: 'Connected',
          description: 'Status chip text for connected integration',
        })}
      />,
    );
  } else if (!isActive) {
    items.push(
      <Button
        key="connect"
        text={intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Connect',
          defaultMessage: 'Connect',
          description: 'Button text to connect an integration',
        })}
        type="submit"
        variant="action"
        onClick={onConnect}
      />,
    );
  } else {
    items.push(
      <Chip
        key="not-connected"
        mode="tag"
        status="info"
        text={intl.formatMessage({
          id: 'StatusAndActions.notConnected',
          defaultMessage: 'Not connected',
          description: 'Status chip text for disconnected integration',
        })}
      />,
    );
  }

  if (
    !isCompanyConnecting &&
    shouldShowDeleteButton(isConnected, integration)
  ) {
    items.push(<Divider key="divider" isHorizontal={false} />);
    items.push(
      <IconButton
        key="delete"
        disabled={isLoading}
        icon="trash"
        text={intl.formatMessage({
          id: 'StatusAndActions.delete',
          defaultMessage: 'Delete',
          description: 'IconButton text to delete an integration',
        })}
        onClick={onDelete}
      />,
    );
  }

  return (
    <Layout align="center" spacing={4}>
      {items}
    </Layout>
  );
};
